<template>
  <div>
    <popup-hint hint="returns">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Returns"/>
      </template>
      <template v-slot:top>
        Returns handle re-entering inventory from a shipment that a customer has returned.
        They are created from a shipment, and shown here.
      </template>
    </popup-hint>

    <purtrak-table name="returns" objectType="returns" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-expand
                   listEndPoint="productReturns2" v-model="selected"
                   fields="id,date,customer{id,name},privateNote,qbo,number,total,exchangeRate,currency,discount{percent,amount,value}"
                   :totals.sync="totals" totalFields="totalValue"
                   :listFilters="listFilters"
                   @dblclick="productReturn => $router.push({name:'SalesReturnsEdit',params:{returnId:productReturn.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   title="Product returns"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',
                                            filters.customerId?'Customer: ' + filters.customerName:'']}"
                   download-endpoint="productReturnsExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.from=null;filters.to=null;filters.customerName=''">
          <v-list-item>
            <filter-search label="Customer name" v-model="filters.customerName"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.customerName" close small class="ml-1" @click:close="filters.customerId=null;filters.customerName=''">{{filters.customerName}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
        </template>
      </template>

      <template v-slot:item.date="{item}">
        {{item.date | formatDateTime}}
      </template>
      <template v-slot:item.customerName="{item,printing}">
        {{item.customer.name}}
        <div v-if="!printing" class="text-caption">{{item.privateNote}}</div>
      </template>
      <template v-slot:item.qbo="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.total="{value,item,printing}">
        {{item.currency===homeCurrency?'':item.currency}} {{value | currency}}
        <div v-if="!printing && item.currency!==homeCurrency" class="text-caption">{{value*item.exchangeRate | currency}}</div>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="200" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Return operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template>
              <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Returns',to:{name:'SalesReturns'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:'',forward:1}}"><v-list-item-title>Trace lot</v-list-item-title></v-list-item>
              <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
<!--
              <v-list-item @click="print('SalesReturnsPrint', item)"><v-list-item-title>Print</v-list-item-title></v-list-item>
-->
              <v-divider/>
              <v-list-item :to="{name:'SalesReturnsEdit',params:{returnId:item.id}}"><v-list-item-title>Edit return</v-list-item-title></v-list-item>
              <v-list-item @click="updatePrivateNote(item)"><v-list-item-title>Update private note</v-list-item-title></v-list-item>
              <v-list-item @click="productReturn=item;showDelete=true"><v-list-item-title>Delete return</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:80%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="40%">Description</th>
                <th :class="expansionPanelClass" width="12%">Lot</th>
                <th class="text-right" :class="expansionPanelClass" width="12%">Quantity</th>
                <th class="text-right" :class="expansionPanelClass" width="12%" v-if="values">Unit price</th>
                <th class="text-right" :class="expansionPanelClass" width="12%" v-if="values">Amount</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="line of item.lines">
                <tr :key="line.key">
                  <td class="text-caption">{{line.description}}</td>
                  <td class="text-caption">
                    <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Returns',to:{name:'SalesReturns'}}]},query:{to:'reports/inventory/ReportsLot',id:line.id,lot:line.lot,forward:0}})">{{line.lot}}</a>
                  </td>
                  <td class="text-caption text-right">{{line.quantity | number(line.precision)}} {{line.units}}</td>
                  <template v-if="!line.items">
                    <td class="text-caption text-right" v-if="values">${{line.unitPrice | number(4)}}</td>
                    <td class="text-caption text-right" v-if="values">{{line.amount | currency}}</td>
                  </template>
                  <td v-else colspan="2"/>
                </tr>
                <tr v-for="bundlineLine of line.items" :key="bundlineLine.key">
                  <td class="text-caption"><span class="ml-4">{{bundlineLine.description}}</span></td>
                  <td class="text-caption">
                    <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Returns',to:{name:'SalesReturns'}}]},query:{to:'reports/inventory/ReportsLot',id:bundlineLine.id,lot:bundlineLine.lot,forward:0}})">{{bundlineLine.lot}}</a>
                  </td>
                  <td class="text-caption text-right">{{line.quantity}}x{{bundlineLine.quantity | number(bundlineLine.precision)}} {{bundlineLine.units}}</td>
                  <td class="text-caption text-right" v-if="values">${{bundlineLine.unitPrice | number(4)}}</td>
                  <td class="text-caption text-right" v-if="values">{{bundlineLine.amount | currency}}</td>
                </tr>
              </template>
              <tr v-if="item.discount.amount || (item.taxLines && item.taxLines.length)">
                <td class="text-caption text-right" colspan="4">Subtotal</td>
                <td class="text-caption text-right">{{item.subTotal | currency}}</td>
              </tr>
              <tr v-if="item.discount.amount">
                <td class="text-caption text-right" colspan="4">Discount
                  <template v-if="item.discount.percent">{{item.discount.amount | number(2)}}%</template>
                  <template v-else>{{item.discount.amount | currency}}</template>
                </td>
                <td class="text-caption text-right">- {{item.discount.value | currency}}</td>
              </tr>
              <template v-for="tax of item.taxLines">
                <tr :key="tax.id">
                  <td class="text-caption text-right" colspan="4">
                    {{tax.name}} @ {{tax.percent}}% on {{tax.onAmount | currency}}
                  </td>
                  <td class="text-caption text-right">{{tax.amount | currency}}</td>
                </tr>
              </template>
              <tr>
                <td class="text-caption text-right" colspan="4">Total</td>
                <td class="text-caption text-right">{{item.total | currency}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile && values">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='total' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <component :is="dialog" v-bind="dialogProperties" :returnId="productReturn.id" @done="dialog = null"/>
    <delete-object v-model="showDelete" name="this return" @delete="performDelete"/>
  </div>
</template>

<script>

export default {
  name: 'SalesReturns',
  data() {
    return {
      filters: {
        from: null,
        to: null,
        customerId: null,
        search: ''
      },
      totals: {totalValue:0},
      dateDisplay: '',
      productReturn: {},
      showDelete: false,
			dialog: null,
      dialogProperties: {},
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'customerName', visible:true},
                       { value: 'total', visible:true},
                       { value: 'qbo', visible:true},
                       { value: 'number', visible:true},
                       { value: 'privateNote', visible:false}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: 'CUSTOMER', value: 'customerName' ,width:'30%',sortable:true},
                { text: 'NOTE', value: 'privateNote' ,width:'30%',sortable:false},
                { text: 'TOTAL', value: 'total' ,width:'15%',align:'right',sortable:true},
                { text: 'QBO', value: 'qbo' ,width:'10%',align:'center',sortable:false, omit:() => !this.company.connectedToAccounting},
                { text: 'NUMBER', value: 'number' ,width:'10%',sortable:false}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    homeCurrency() {
      return this.company.settings.homeCurrency;
    },
    readOnly() {
      return !this.company.permissions.includes('selling.write');
    },
    values() {
      return this.company.permissions.includes('values');
    },
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.customerId !== null)
        filters.push({field:'customerId', comparator:'eq', value:this.filters.customerId});
      if (this.filters.customerName)
        filters.push({field:'customerName', comparator:'contains', value:this.filters.customerName});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
  },
  methods: {
    async expand(item, value) {
      if (value && !item.lines) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesReturns($id:ID!) {
                                                productReturn(id:$id) {
                                                  subTotal
                                                  lines {
                                                    description,quantity,units,precision,unitPrice,amount,lot,id
                                                    items {description,quantity,units,precision,unitPrice,amount,lot,id}
                                                  }
                                                  taxLines {id,name,amount,onAmount,percent}
                                                }
                                              }`,
                                            {id:item.id});
        item.subTotal = results.data.productReturn.subTotal;
        this.$set(item, 'lines', results.data.productReturn.lines);
        this.$set(item, 'taxLines', results.data.productReturn.taxLines);
        let key = 1;
        item.lines.forEach(line => line.key = key++);
        this.loading = false;
      }
    },
    updatePrivateNote(salesReturn) {
      this.dialogProperties = {item:salesReturn, operation:'productReturnUpdatePrivateNote'};
      this.dialog = () => import(`@/components/UpdatePrivateNote`);
    },
    async performDelete() {
      this.loading = true;
      const results = await this.$apiCall(`mutation SalesReturns($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.productReturn.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted return for ${this.productReturn.customer.name}`});
      }
    },
    print(dialog, productReturn) {
      this.productReturn = productReturn;
      this.dialog = () => import(`@/views/sales/shipments/${dialog}`);
    }
  }
}
</script>